import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';
import { ProjectDetailsApiService } from '../../services/project-details-api.service';
import { ProjectDetailsStateService } from '../../services/project-details-state.service';
import { ProjectDetailsCascadeService } from '../../services/project-details-cascade.service';
import { ThemeAllocationDailogComponent } from '../../project-details-dialog/theme-allocation-dailog/theme-allocation-dailog.component';
import { commonFunctions } from '../../../common/classes/common.class';

@Component({
  selector: 'app-invoice-prepayment-tab',
  templateUrl: './invoice-prepayment-tab.component.html',
  styleUrls: ['./invoice-prepayment-tab.component.scss']
})
export class InvoicePrepaymentTabComponent implements OnInit, OnDestroy {
  invoiceForm!: FormGroup;
  prepaymentForm!: FormGroup;

  invoiceFormatsList: string[] = [];
  periodTypesList: string[] = [];
  typesOfServiceList: string[] = [];
  purchaseOrderList: string[] = [];

  currencyLabel: string = '';
  taxTypeLabel: string = 'VAT Rate: (%)';

  // Maps the dropdown's display text (PO_Long_Description) back to the
  // real PO code (PO_Number) exactly as usp_GetPONumber returns it.
  // No parsing/guessing of the code from display text happens anywhere.
  private poDescToCode = new Map<string, string>();

  private subs = new Subscription();

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private apiService: ProjectDetailsApiService,
    public stateService: ProjectDetailsStateService,
    public cascadeService: ProjectDetailsCascadeService
  ) {}

  ngOnInit(): void {
    this.buildForms();
    this.subscribeToState();
    this.loadReferenceData();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  buildForms(): void {
    this.invoiceForm = this.fb.group({
      invoiceDescription: ['', Validators.required],
      projectDiscount: [0, [Validators.required, Validators.min(0)]],
      mileageRate: [null],
      projectHourlyRate: ['0.00', [Validators.required, Validators.min(0)]],
      showHourlyBreakdown: [false],
      salesTax: [0, Validators.required],
      charge: [true],
      promptPaymentPeriod: [null],
      promptPaymentDiscount: [null],
      promptPaymentPeriodType: [null],
      noVat: [false],
      clientVatNo: [{ value: '', disabled: true }],
      invoiceFormat: ['Free Format', Validators.required],
      invoiceContact: ['', Validators.required],
      invoiceContactAddress: ['', Validators.required],
      freeFormatDescription: [{ value: '', disabled: true }],
      cnpj: [{ value: '', disabled: true }],
      typeOfService: [{ value: null, disabled: true }],
      purchaseOrderNoViewMode: [null]
    });

    this.prepaymentForm = this.fb.group({
      // Always readonly on the template side. It mirrors Project.[Purchase Order Number]
      // directly (see populateInvoiceDetails) and is never driven by the Invoice dropdown.
      prepaymentPurchaseOrderNo: [''],
      prepaymentAmt: [null, [Validators.required, Validators.min(0)]],
      referenceNo: ['', Validators.required],
      prepaymentDate: [null, Validators.required],
      prepaymentDescription: ['', Validators.required]
    });

    // Invoice Format -> Free Format Description conditional state
    this.invoiceForm.get('invoiceFormat')?.valueChanges.subscribe(() => {
      this.updateFreeFormatDescription();
    });

    // No VAT -> Client VAT No conditional state
    this.invoiceForm.get('noVat')?.valueChanges.subscribe(() => {
      this.updateClientVatNo();
    });

    // NOTE: There is intentionally NO subscription here syncing
    // purchaseOrderNoViewMode -> prepaymentPurchaseOrderNo.
    // Verified against usp_UpdateProjectPrePaymentDetail: the Prepayment
    // PO can never be changed after creation, and it is sourced from
    // Project.[Purchase Order Number], not from the Invoice dropdown
    // selection. Changing the Invoice dropdown must NOT touch the
    // Prepayment PO field.

    this.invoiceForm.valueChanges.subscribe(() => {
      if (this.invoiceForm.dirty) {
        this.stateService.setUnsavedChanges(true);
      }
    });

    this.prepaymentForm.valueChanges.subscribe(() => {
      if (this.prepaymentForm.dirty) {
        this.stateService.setUnsavedChanges(true);
      }
    });
  }

  private updateFreeFormatDescription(): void {
    const invoiceFormat = this.invoiceForm.get('invoiceFormat')?.value;
    const freeFormatControl = this.invoiceForm.get('freeFormatDescription');

    if (invoiceFormat === 'Free Format') {
      freeFormatControl?.enable({ emitEvent: false });
      freeFormatControl?.setValidators(Validators.required);
    } else {
      freeFormatControl?.reset('', { emitEvent: false });
      freeFormatControl?.disable({ emitEvent: false });
      freeFormatControl?.clearValidators();
    }

    freeFormatControl?.updateValueAndValidity({ emitEvent: false });
  }

  private updateClientVatNo(): void {
    const noVat = !!this.invoiceForm.get('noVat')?.value;
    const clientVatControl = this.invoiceForm.get('clientVatNo');

    if (noVat) {
      clientVatControl?.enable({ emitEvent: false });
    } else {
      clientVatControl?.reset('', { emitEvent: false });
      clientVatControl?.disable({ emitEvent: false });
    }

    clientVatControl?.updateValueAndValidity({ emitEvent: false });
  }

  private updateCnpjAndTosState(company: string): void {
    const isBrazil = (company || '').toLowerCase().includes('brazil');
    const cnpjControl = this.invoiceForm.get('cnpj');
    const tosControl = this.invoiceForm.get('typeOfService');

    if (isBrazil) {
      cnpjControl?.enable({ emitEvent: false });
      cnpjControl?.setValidators(Validators.required);
      tosControl?.disable({ emitEvent: false });
    } else {
      cnpjControl?.reset('', { emitEvent: false });
      cnpjControl?.disable({ emitEvent: false });
      cnpjControl?.clearValidators();

      tosControl?.reset(null, { emitEvent: false });
      tosControl?.disable({ emitEvent: false });
    }

    cnpjControl?.updateValueAndValidity({ emitEvent: false });
    tosControl?.updateValueAndValidity({ emitEvent: false });
  }

  subscribeToState(): void {
    this.subs.add(
      this.stateService.currencyLabel$.subscribe(label => {
        this.currencyLabel = label;
      })
    );

    this.subs.add(
      this.stateService.taxTypeLabel$.subscribe(label => {
        this.taxTypeLabel = label;
      })
    );

    this.subs.add(
      this.stateService.defaultTaxRate$.subscribe(rate => {
        const currentTax = this.invoiceForm.get('salesTax')?.value;
        if (currentTax === 0 || currentTax === null || currentTax === undefined) {
          this.invoiceForm.patchValue({ salesTax: rate }, { emitEvent: false });
        }
      })
    );

    this.subs.add(
      this.stateService.tableData$.subscribe(data => {
        if (data.invoiceDetails || data.project_details) {
          this.populateInvoiceDetails(data.invoiceDetails);
        }
        // loadPurchaseOrders() must run AFTER populateInvoiceDetails(), since it
        // needs tableData.invoiceDetails.purchaseOrderNumber to try to match
        // against the fetched PO list.
        this.loadPurchaseOrders();
        this.cdr.detectChanges();
      })
    );

    // Add mode: autofill Invoice Contact / Invoice Contact Address as soon as
    // a Contact is selected on the main Project Details tab.
    this.subs.add(
      this.stateService.selectedContactDetails$.subscribe(contact => {
        if (!contact) return;
        this.invoiceForm.patchValue({
          invoiceContact: contact.name || '',
          invoiceContactAddress: contact.address || ''
        }, { emitEvent: false });
      })
    );

    // Add mode: recompute Tax whenever Country/State changes on the main tab.
    this.subs.add(
      this.stateService.selectedCountryCode$.subscribe(country => {
        const state = this.stateService.getSelectedCountryName ? '' : '';

        const taxPct = this.cascadeService.getTaxPercentage(country, '');
        this.stateService.setDefaultTaxRate(taxPct);

        this.stateService.setTaxTypeLabel(
          this.cascadeService.getTaxTypeForCountry(country)
        );
      })
    );
  }

  private extractDisplayValue(item: any, keys: string[]): string | null {
    if (item === null || item === undefined) return null;
    if (typeof item === 'string') return item;
    if (typeof item === 'number') return String(item);

    for (const key of keys) {
      const val = item[key];
      if (val !== null && val !== undefined && val !== '') {
        return String(val);
      }
    }

    const firstStringValue = Object.values(item).find(
      (v) => typeof v === 'string' && v.trim() !== ''
    );

    return firstStringValue ? String(firstStringValue) : null;
  }

  async loadReferenceData(): Promise<void> {
    try {
      const [formats, periodTypes, typesOfService] = await Promise.all([
        this.apiService.getInvoiceFormats(),
        this.apiService.getPromptPaymentPeriodTypes(),
        this.apiService.getTypesOfService()
      ]);

      this.invoiceFormatsList = (formats || [])
        .map((f: any) => this.extractDisplayValue(f, [
          'Invoice_Format', 'invoiceFormat', 'InvoiceFormat', 'invoice_format',
          'Format', 'format', 'Description', 'description', 'Name', 'name'
        ]))
        .filter((v): v is string => !!v);

      this.periodTypesList = (periodTypes || [])
        .map((p: any) => this.extractDisplayValue(p, [
          'Period_Type', 'periodType', 'PeriodType', 'period_type',
          'Type', 'type', 'Description', 'description', 'Name', 'name'
        ]))
        .filter((v): v is string => !!v);

      this.typesOfServiceList = (typesOfService || [])
        .map((t: any) => this.extractDisplayValue(t, [
          'Type_Of_Service', 'typeOfService', 'TypeOfService', 'type_of_service',
          'Service', 'service', 'Description', 'description', 'Name', 'name'
        ]))
        .filter((v): v is string => !!v);

      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error loading invoice ref data:', e);
    }
  }

  /**
   * Loads the "available / active" Purchase Orders for this project from
   * usp_GetPONumber (Purchase_Order + PO_Mapping). This is a SEPARATE data
   * source from Project.[Purchase Order Number] - a project can have a
   * saved PO that never appears here (no PO_Mapping row, inactive, etc).
   *
   * Add mode: usp_AddProject2 / the old PO-NO-ADD field confirm there is
   * no PO concept yet for a brand-new project, so no API call is made and
   * the field is shown disabled with "Not Applicable", matching old code.
   */
  async loadPurchaseOrders(): Promise<void> {
    const mode = this.stateService.getMode();
    const clientCode = this.stateService.getSelectedClientCode();
    const projectCode = this.stateService.getSelectedProjectCode();

    if (mode === 'add' || !clientCode || !projectCode) {
      this.purchaseOrderList = [];
      this.poDescToCode.clear();
      this.invoiceForm.patchValue({ purchaseOrderNoViewMode: 'Not Applicable' }, { emitEvent: false });
      this.invoiceForm.get('purchaseOrderNoViewMode')?.disable({ emitEvent: false });
      return;
    }

    this.invoiceForm.get('purchaseOrderNoViewMode')?.enable({ emitEvent: false });

    try {
      const pos = await this.apiService.getPurchaseOrdersForProject(clientCode, projectCode);
      const poList: string[] = [];
      this.poDescToCode.clear();

      if (Array.isArray(pos)) {
        for (const p of pos) {
          // usp_GetPONumber's real column names, with fallbacks kept for safety
          const code = p.PO_Number || p.PurchaseOrderNumber || p.purchaseOrderNumber || '';
          const desc = p.PO_Long_Description || p.PurchaseOrderLongDescription || code;

          poList.push(desc);
          this.poDescToCode.set(desc, code);
        }
      }

      this.purchaseOrderList = poList;

      // Try to pre-select the project's ACTUAL saved PO (Project.[Purchase Order
      // Number], read via tableData.invoiceDetails.purchaseOrderNumber) against
      // this fetched list, matching on the real code only - no string
      // reconstruction. If there is no match, leave the dropdown unselected;
      // that mirrors old behavior exactly (a saved PO not present in
      // Purchase_Order/PO_Mapping was never force-matched in the old app either).
      const tableData = this.stateService.getTableData();
      const inv = tableData.invoiceDetails || {};
      const savedCode = inv.purchaseOrderNumber || inv.purchaseOrderNo || '';

      if (savedCode) {
        const matchEntry = [...this.poDescToCode.entries()].find(([, c]) => c === savedCode);
        if (matchEntry) {
          this.invoiceForm.patchValue({ purchaseOrderNoViewMode: matchEntry[0] }, { emitEvent: false });
        }
      }
    } catch (e) {
      this.purchaseOrderList = [];
      console.error('Error loading purchase orders:', e);
    }
  }

  populateInvoiceDetails(inv: any): void {
    const tableData = this.stateService.getTableData();
    const projectDetails = tableData.project_details || {};
    const contactData = projectDetails.contactData || {};
    const details = inv || {};

    const isNoVat = commonFunctions.Stringtoboolean(details.noVat);
    const invoiceFormat = details.invoiceFormat || 'Free Format';
    const rawPoNo = details.purchaseOrderNumber ?? details.purchaseOrderNo ?? '';

    const invDesc = details.invoiceDesc ?? details.invoiceDescription ?? projectDetails.description ?? projectDetails.projectDescription ?? '';
    const freeFormatDesc = details.freeFormatInvoiceDescription ?? details.freeFormatDescription ?? invDesc;
    const invContact = details.invoiceContact || projectDetails.contact || '';
    const invAddress = details.invoiceAddress ?? details.invoiceContactAddress ?? contactData.address ?? contactData.Address ?? '';

    const rate = details.rate !== undefined && details.rate !== null ? details.rate : (details.projectHourlyRate !== undefined && details.projectHourlyRate !== null ? details.projectHourlyRate : '0.00');
    const discount = details.discount !== undefined && details.discount !== null ? details.discount : (details.projectDiscount !== undefined && details.projectDiscount !== null ? details.projectDiscount : 0);
    const salesTax =
      details.vatRate !== undefined && details.vatRate !== null
        ? Number(details.vatRate) * 100
        : (
            details.salesTax !== undefined && details.salesTax !== null
              ? Number(details.salesTax) * 100
              : this.stateService.getDefaultTaxRate()
          );
    const chargeVal = details.charge !== undefined ? commonFunctions.Stringtoboolean(details.charge) : true;

    this.invoiceForm.patchValue({
      invoiceDescription: invDesc,
      projectDiscount: discount,
      mileageRate: details.mileage ?? details.mileageRate ?? null,
      projectHourlyRate: rate,
      showHourlyBreakdown: commonFunctions.Stringtoboolean(details.showHourBreakdown ?? details.showHourlyBreakdown ?? false),
      salesTax: salesTax,
      charge: chargeVal,
      promptPaymentPeriod: details.promptPaymentPeriodDuration ?? details.promptPaymentPeriod ?? null,
      promptPaymentDiscount: details.promptPaymentPeriodDiscount ?? details.promptPaymentDiscount ?? null,
      promptPaymentPeriodType: details.promptPaymentPeriodType || null,
      noVat: isNoVat,
      clientVatNo: details.clientVatNumber ?? details.clientVatNo ?? '',
      invoiceFormat: invoiceFormat,
      invoiceContact: invContact,
      invoiceContactAddress: invAddress,
      freeFormatDescription: freeFormatDesc,
      cnpj: details.cnpj || '',
      typeOfService: details.typeOfService || null
      // purchaseOrderNoViewMode is intentionally NOT set here. loadPurchaseOrders()
      // owns it exclusively, because it can only be set correctly once the real
      // PO list (usp_GetPONumber) has been fetched and matched against.
    }, { emitEvent: false });

    const company = projectDetails.company || projectDetails.billingCompany || '';
    this.updateCnpjAndTosState(company);

    this.updateFreeFormatDescription();
    this.updateClientVatNo();

    // Prepayment PO ALWAYS mirrors Project.[Purchase Order Number] directly -
    // verified via usp_UpdateProject2 / usp_AddProject2 (write side) and the
    // matching raw DB value seen against this exact field on screen. This is
    // completely independent of the Invoice dropdown / usp_GetPONumber list.
    this.prepaymentForm.patchValue(
      { prepaymentPurchaseOrderNo: rawPoNo || 'Not Applicable' },
      { emitEvent: false }
    );
  }

  /**
   * Selecting a different PO in the Invoice dropdown is purely a display
   * choice for the Invoice form. Verified via usp_UpdateProjectPrePaymentDetail:
   * there is no backend code path that changes a Prepayment's PO after it is
   * created, and the Prepayment PO field is sourced from
   * Project.[Purchase Order Number], not from this dropdown. So this handler
   * intentionally does NOT touch prepaymentForm.
   */
  onPurchaseOrderChange(poValue: string): void {
    // No-op by design. Kept as a hook in case the Invoice form itself
    // needs to react to PO selection in the future.
  }

  // ==========================================
  // Form Actions (Submit / Clear / View)
  // ==========================================

  async onSubmitInvoice(): Promise<void> {
    if (this.invoiceForm.invalid) {
      this.invoiceForm.markAllAsTouched();
      alert('Please fill all required invoice fields.');
      return;
    }

    const formVal = this.invoiceForm.getRawValue();
    const clientCode = this.stateService.getSelectedClientCode();
    const projectCode = this.stateService.getSelectedProjectCode();
    const tableData = this.stateService.getTableData();
    const projectDetails = tableData.project_details || {};

    // The dropdown shows a description string ("PO_Long_Description"), but
    // the backend (Project.[Purchase Order Number]) needs the real code
    // ("PO_Number"). Resolve through the map before building the payload.
    const selectedPoDesc = formVal.purchaseOrderNoViewMode;
    const purchaseOrderNumberForBackend =
      this.poDescToCode.get(selectedPoDesc) ?? selectedPoDesc ?? '';
    console.log('manager being sent:', JSON.stringify(projectDetails.manager), 'full project_details:', projectDetails);
    const payload = {
      client: clientCode,
      projectCode: projectCode,
      description: projectDetails.description || '',
      contact: projectDetails.contact || '',
      manager: projectDetails.projectManager || '',
      discount: formVal.projectDiscount ? parseInt(formVal.projectDiscount, 10) : 0,
      discountType: 'Percentage',
      invoiceDesc: formVal.invoiceDescription || '',
      mileage: formVal.mileageRate ? parseInt(formVal.mileageRate, 10) : 0,
      purchaseOrderNumber: purchaseOrderNumberForBackend,
      rate: formVal.projectHourlyRate ? parseInt(formVal.projectHourlyRate, 10) : 0,
      showHourBreakdown: commonFunctions.booleanToString(formVal.showHourlyBreakdown),
      vatRate: formVal.salesTax ? parseInt(formVal.salesTax, 10) : 0,
      charge: commonFunctions.booleanToString(formVal.charge),
      oldCode: commonFunctions.booleanToInteger(commonFunctions.Stringtoboolean(projectDetails.oldCode)),
      promptPaymentPeriodDuration: formVal.promptPaymentPeriod ? parseInt(formVal.promptPaymentPeriod, 10) : 0,
      promptPaymentPeriodDiscount: formVal.promptPaymentDiscount ? parseInt(formVal.promptPaymentDiscount, 10) : 0,
      promptPaymentPeriodType: await this.apiService.getPromptPaymentPeriodTypeId(formVal.promptPaymentPeriodType),
      loginName: this.stateService.loginName,
      internal: commonFunctions.booleanToInteger(commonFunctions.Stringtoboolean(projectDetails.internal)),
      noVat: !!formVal.noVat,
      clientVatNumber: formVal.clientVatNo || '',
      companyId: await this.apiService.getCompanyId(projectDetails.company),
      invoiceFormat: await this.apiService.getInvoiceFormatId(formVal.invoiceFormat),
      invoiceContact: formVal.invoiceContact || '',
      invoiceAddress: formVal.invoiceContactAddress || '',
      freeFormatInvoiceDescription: formVal.freeFormatDescription || '',
      paymentDays: projectDetails.paymentDays ?? 30,
      projectTypeId: await this.apiService.getProjectTypeId(projectDetails.projectType),
      sensitive: commonFunctions.booleanToInteger(true),
      cnpj: formVal.cnpj || '',
      typeOfService: formVal.typeOfService || '',
      oldProject: projectCode
    };

    try {
      const res = await this.apiService.updateProject(payload).toPromise();
      this.stateService.showNotification(res);
      this.stateService.setUnsavedChanges(false);

      const refreshedData = await this.apiService.getProjectDetailsTables(clientCode, projectCode);
      this.stateService.setTableData(refreshedData);
    } catch (err: any) {
      this.stateService.showNotification(err.error || { Type: 'failure', Message: 'Failed to update invoice details' });
    }
  }

  onClearInvoice(): void {
    const defaultTaxRate = this.stateService.getDefaultTaxRate();
    this.invoiceForm.reset({
      salesTax: defaultTaxRate,
      showHourlyBreakdown: false,
      charge: false,
      noVat: false
    });
    this.updateFreeFormatDescription();
    this.updateClientVatNo();
  }

  async onSubmitPrepayment(): Promise<void> {
    if (this.prepaymentForm.invalid) {
      this.prepaymentForm.markAllAsTouched();
      alert('Please fill all required prepayment fields.');
      return;
    }

    const formVal = this.prepaymentForm.getRawValue();
    const clientCode = this.stateService.getSelectedClientCode();
    const projectCode = this.stateService.getSelectedProjectCode();

    const poForSubmit =
      formVal.prepaymentPurchaseOrderNo === 'Not Applicable' ? '' : (formVal.prepaymentPurchaseOrderNo || '');

    const payload = {
      client: clientCode,
      project: projectCode,
      purchaseOrderNo: poForSubmit,
      prepaymentAmt: parseInt(formVal.prepaymentAmt, 10),
      referenceNo: formVal.referenceNo,
      prepaymentDate: moment(formVal.prepaymentDate).format('YYYY-MM-DD'),
      description: formVal.prepaymentDescription || '',
      currentUser: this.stateService.loginName
    };

    try {
      const res = await this.apiService.savePrepaymentDetails(payload).toPromise();
      this.stateService.showNotification(res);
      this.prepaymentForm.reset({
        prepaymentPurchaseOrderNo: formVal.prepaymentPurchaseOrderNo,
        prepaymentDate: null
      });

      const data = await this.apiService.getProjectDetailsTables(clientCode, projectCode);
      this.stateService.setTableData(data);
    } catch (err: any) {
      this.stateService.showNotification(err.error || { Type: 'failure', Message: 'Failed to save prepayment details' });
    }
  }

  onOpenPrepaymentDialog(): void {
    this.dialog.open(ThemeAllocationDailogComponent, {
      width: '800px',
      height: '500px',
      data: {
        gridType: 'prepaymentDetails',
        mode: 'edit'
      }
    });
  }
}